// src/components/VoorschotEmailGenerator.js
import React, { useState, useEffect, useMemo } from 'react';
import '../css/VoorschotEmailGenerator.css';
import api from './api';
import generateEmailHtml from './Template';
import { 
  calculatePredictedTotal, 
  isCurrentEnergyYear, 
  getLatestMonthWithData 
} from '../utils/energyCalculations';

const VoorschotEmailGenerator = ({ selectedEnergyYear }) => {
  const [apartments, setApartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [apartmentUsage, setApartmentUsage] = useState({});
  const [residentsData, setResidentsData] = useState({});
  const [selectedApartments, setSelectedApartments] = useState(new Set());
  const [emailPreview, setEmailPreview] = useState(null);
  const [previewApartment, setPreviewApartment] = useState(null);
  const [sendingEmails, setSendingEmails] = useState(false);
  const [sendResults, setSendResults] = useState(null);
  const [priceSettings, setPriceSettings] = useState({
    variablePricePerUnit: 0.600054,
    fixedPricePerUnit: 0.717296
  });
  const [lastImportDate, setLastImportDate] = useState(null);

  // NEW: Sorting state
  const [sortConfig, setSortConfig] = useState({ key: 'apartmentNumber', direction: 'asc' });

  // Apartment size configuration
  const APARTMENT_SIZES = {
    SMALL: 250,  // Small apartments get 250 fixed units
    LARGE: 375   // Large apartments get 375 fixed units
  };

  // Helper function to normalize apartment numbers
  const normalizeApartmentNumber = (aptNum) => {
    // If it's already a string, return it
    if (typeof aptNum === 'string') {
      return aptNum;
    }
    // If it's a number, convert to string
    if (typeof aptNum === 'number') {
      return String(aptNum);
    }
    // Otherwise return as-is
    return aptNum;
  };

  // Debug key matches between apartments and usage data
  const debugKeyMatches = () => {
    console.log('🔍 Checking key matches between apartments and usage data');
    
    if (Object.keys(apartmentUsage).length === 0) {
      console.error('❌ No usage data available yet!');
      return;
    }
    
    if (apartments.length === 0) {
      console.error('❌ No apartment data available yet!');
      return;
    }
    
    console.log(`Apartments count: ${apartments.length}`);
    console.log(`Usage data count: ${Object.keys(apartmentUsage).length}`);
    
    console.log('Usage data keys:', Object.keys(apartmentUsage));
    console.log('Apartment numbers:', apartments.map(apt => apt.apartmentNumber));
    
    const mismatches = [];
    const matches = [];
    
    apartments.forEach(apartment => {
      const aptNum = apartment.apartmentNumber;
      const hasData = !!apartmentUsage[aptNum];
      
      if (hasData) {
        matches.push(aptNum);
      } else {
        mismatches.push(aptNum);
      }
    });
    
    console.log(`✅ Matched: ${matches.length} apartments`);
    console.log(`❌ Mismatched: ${mismatches.length} apartments`);
    
    if (mismatches.length > 0) {
      console.error('Apartments with no matching usage data:', mismatches);
      
      // Try to find possible matches
      mismatches.forEach(aptNum => {
        // Try numeric comparison
        const numericAptNum = parseInt(aptNum);
        if (!isNaN(numericAptNum)) {
          const numericMatches = Object.keys(apartmentUsage).filter(key => {
            const numericKey = parseInt(key);
            return !isNaN(numericKey) && numericKey === numericAptNum;
          });
          
          if (numericMatches.length > 0) {
            console.log(`Apartment ${aptNum} has numeric matches:`, numericMatches);
          }
        }
      });
    }
  };

  // Load all necessary data
  useEffect(() => {
    if (selectedEnergyYear) {
      loadAllData();
    }
  }, [selectedEnergyYear]);

  const loadAllData = async () => {
    setLoading(true);
    try {
      // First fetch the last import date
      await fetchLastImportDate();
      
      // Then load the rest of the data in parallel
      await Promise.all([
        loadPriceSettings(),
        loadResidentsData()
      ]);
      
      // Load voorschot data next
      await loadVoorschotData();
      
      // Load usage data last, after we have the lastImportDate
      await loadUsageData();
      
      // After all data is loaded, run debugging check
      setTimeout(() => {
        console.log('Running debug key matches check...');
        debugKeyMatches();
      }, 1000);
    } catch (error) {
      console.error('❌ Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLastImportDate = async () => {
    try {
      const response = await api.get('/api/ista/last-date');
      if (response.data && response.data.lastDate) {
        setLastImportDate(response.data.lastDate);
      }
    } catch (error) {
      console.error('Error fetching last import date:', error);
    }
  };

  const loadPriceSettings = async () => {
    try {
      const response = await api.get(`/api/price-settings/${selectedEnergyYear}`);
      if (response.data.success) {
        setPriceSettings({
          variablePricePerUnit: response.data.data.variablePricePerUnit,
          fixedPricePerUnit: response.data.data.fixedPricePerUnit
        });
      }
    } catch (error) {
      console.error('❌ Error loading price settings:', error);
    }
  };

  // UPDATED: Filter only for Eigenaar residents
  const loadResidentsData = async () => {
    try {
      const response = await api.get('/api/bewoners/get-all');
      if (response.data && Array.isArray(response.data)) {
        const residentsMap = {};
        response.data.forEach(resident => {
          const apartmentNum = normalizeApartmentNumber(resident.nummer);
          if (apartmentNum && parseInt(apartmentNum) >= 211 && parseInt(apartmentNum) <= 305) {
            if (!residentsMap[apartmentNum]) {
              residentsMap[apartmentNum] = [];
            }
            // UPDATED: Only include current residents who are "Eigenaar" and have email
            if (!resident.isFormerResident && 
                resident.inhabited && 
                resident.email && 
                resident.rol === 'Eigenaar') {
              residentsMap[apartmentNum].push({
                naam: resident.naam,
                email: resident.email,
                rol: resident.rol
              });
            }
          }
        });
        setResidentsData(residentsMap);
        console.log(`📋 Loaded ${Object.keys(residentsMap).length} apartments with Eigenaar residents having email`);
      }
    } catch (error) {
      console.error('❌ Error loading residents data:', error);
    }
  };

  // UPDATED: Use the same prediction calculation method as other components
  const loadUsageData = async () => {
    try {
      console.log('📊 Loading usage data...');
      
      const response = await api.get('/api/ista/compare-energy-years');
      console.log('Response received:', response.data ? `${response.data.length} items` : 'No data');
      
      if (response.data && Array.isArray(response.data)) {
        // Create an initial map with all homeNumbers
        const usageMap = {};
        
        // Filter to only include apartments in the tower (211-305)
        const torenApartments = response.data.filter(apt => {
          const aptNum = parseInt(apt.homeNumber);
          return aptNum >= 211 && aptNum <= 305;
        });
        
        console.log(`Found ${torenApartments.length} tower apartments in data`);
        
        // Calculate energy year keys
        const [currentStartYear, currentEndYear] = selectedEnergyYear.split('-').map(Number);
        const previousEnergyYear = `${currentStartYear - 1}-${currentEndYear - 1}`;
        console.log(`Current energy year: ${selectedEnergyYear}, Previous: ${previousEnergyYear}`);
        
        // Process each apartment
        torenApartments.forEach(apartment => {
          // Normalize apartment number
          const aptNum = normalizeApartmentNumber(apartment.homeNumber);
          console.log(`Processing apartment ${aptNum}`);
          
          // Get all valid year keys (totalYYYY_YYYY)
          const yearKeys = Object.keys(apartment)
            .filter(k => k.match(/^total\d{4}_\d{4}$/))
            .sort();
          
          if (yearKeys.length > 0) {
            // Get the current energy year data
            const targetYear = selectedEnergyYear.replace('-', '_');
            const currentYearKey = yearKeys.find(k => k.includes(targetYear));
            
            // Get the EXACT previous energy year data
            const previousYearTarget = previousEnergyYear.replace('-', '_');
            const previousYearKey = yearKeys.find(k => k.includes(previousYearTarget));
            
            // Get monthly data for current year
            const currentYearPattern = selectedEnergyYear;
            const currentYearMonthlyData = apartment[currentYearPattern] || {};
            
            // Calculate actual usage so far
            const actualSoFar = Object.values(currentYearMonthlyData).reduce((sum, val) => sum + (val || 0), 0);
            
            // Get the latest month with data
            const latestMonth = getLatestMonthWithData(currentYearMonthlyData);
            
            // Use the unified prediction function
            const predictedTotal = calculatePredictedTotal(
              actualSoFar,
              latestMonth,
              lastImportDate,
              isCurrentEnergyYear(selectedEnergyYear)
            );
            
            // Debug log for apartment 237
            if (apartment.homeNumber === '237') {
              console.log(`🔍 Apartment 237 prediction:`, {
                actualSoFar,
                latestMonth,
                lastImportDate: lastImportDate ? new Date(lastImportDate).toLocaleDateString() : null,
                isCurrentYear: isCurrentEnergyYear(selectedEnergyYear),
                predictedTotal
              });
            }
            
            // Get the actual values
            const currentYearTotal = currentYearKey ? apartment[currentYearKey] : actualSoFar;
            const previousYearTotal = previousYearKey ? apartment[previousYearKey] : null;
            
            // Store all data in usage map
            usageMap[aptNum] = {
              currentYearActual: currentYearTotal,
              previousYearTotal: previousYearTotal,
              predictedTotal: predictedTotal,
              actualSoFar: actualSoFar,
              monthlyData: currentYearMonthlyData,
              latestMonth: latestMonth
            };
          } else {
            console.log(`No valid year keys found for apartment ${aptNum}`);
          }
        });
        
        // Normalize the map to ensure consistent key types
        const normalizedMap = {};
        Object.entries(usageMap).forEach(([key, value]) => {
          normalizedMap[normalizeApartmentNumber(key)] = value;
        });
        
        console.log(`Usage map created with ${Object.keys(normalizedMap).length} apartments`);
        
        // Check if we need to create any fallback entries for displayed apartments
        if (apartments && apartments.length > 0) {
          const missingApartments = apartments
            .map(apt => apt.apartmentNumber)
            .filter(aptNum => !normalizedMap[aptNum]);
          
          if (missingApartments.length > 0) {
            console.warn(`${missingApartments.length} apartments are missing data, creating fallbacks:`, missingApartments);
            
            missingApartments.forEach(aptNum => {
              normalizedMap[aptNum] = {
                currentYearActual: 300, // Default fallback
                previousYearTotal: 300,
                predictedTotal: 600,
                actualSoFar: 300,
                latestMonth: null
              };
            });
          }
        }
        
        setApartmentUsage(normalizedMap);
      } else {
        console.error('Invalid response format from API');
      }
    } catch (error) {
      console.error('❌ Error loading usage data:', error);
    }
  };

  const loadVoorschotData = async () => {
    try {
      const response = await api.get(`/api/voorschotten/energy-year/${selectedEnergyYear}`);
      if (response.data.apartments) {
        const apartmentList = response.data.apartments
          .filter(apt => {
            const aptNum = parseInt(apt.apartmentNumber);
            return aptNum >= 211 && aptNum <= 305;
          })
          .map(apt => ({
            ...apt,
            // Normalize the apartment number to string for consistent lookup
            apartmentNumber: normalizeApartmentNumber(apt.apartmentNumber),
            hasEigenaarEmail: residentsData[apt.apartmentNumber]?.some(r => r.email && r.rol === 'Eigenaar')
          }));
        
        console.log(`Loaded ${apartmentList.length} apartments with voorschot data`);
        console.log(`Apartment numbers:`, apartmentList.map(apt => apt.apartmentNumber));
        
        setApartments(apartmentList);
      }
    } catch (error) {
      console.error('❌ Error loading voorschot data:', error);
    }
  };

  // NEW: Sorting function with additional column support
  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // NEW: Get sort indicator
  const getSortIndicator = (columnKey) => {
    if (sortConfig.key !== columnKey) return ' ↕️';
    return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
  };

  // Calculate analysis status for an apartment (same logic as VoorschotAnalysis)
  const calculateApartmentStatus = (apartment) => {
    const aptNum = apartment.apartmentNumber;
    console.log(`🔍 Calculating status for apartment ${aptNum}`);
    
    // Debug the usage data for this apartment
    const usage = apartmentUsage[aptNum];
    console.log(`Usage data for ${aptNum}:`, usage);
    
    // Check if the apartment number exists as a different type (number vs string)
    if (!usage) {
      // Try to find the apartment by converting the key
      const numericAptNum = parseInt(aptNum);
      const stringAptNum = String(aptNum);
      
      console.log(`Looking for alternative keys: ${numericAptNum} or "${stringAptNum}"`);
      
      // Check if it exists as numeric key
      const numericKeyExists = apartmentUsage.hasOwnProperty(numericAptNum);
      console.log(`Exists as numeric key (${numericAptNum}): ${numericKeyExists}`);
      
      // Check if it exists as string key
      const stringKeyExists = apartmentUsage.hasOwnProperty(stringAptNum);
      console.log(`Exists as string key ("${stringAptNum}"): ${stringKeyExists}`);
      
      if (numericKeyExists) {
        console.log(`Found apartment ${aptNum} usage with numeric key!`);
        return calculateApartmentStatusWithUsage(apartment, apartmentUsage[numericAptNum]);
      } else if (stringKeyExists) {
        console.log(`Found apartment ${aptNum} usage with string key!`);
        return calculateApartmentStatusWithUsage(apartment, apartmentUsage[stringAptNum]);
      }
      
      console.error(`❌ No usage data found for apartment ${aptNum}`);
      return { status: 'Onbekend', statusColor: 'gray' };
    }
    
    // If we found the usage, proceed with calculation
    return calculateApartmentStatusWithUsage(apartment, usage);
  };

  // Helper function that does the actual calculation once we have valid usage data
  const calculateApartmentStatusWithUsage = (apartment, usage) => {
    const aptNum = apartment.apartmentNumber;
    
    // Determine if this is current energy year
    const currentYear = isCurrentEnergyYear(selectedEnergyYear);
    
    console.log(`Energy year check for ${aptNum}: isCurrentEnergyYear=${currentYear}`);

    // Calculate total voorschotten
    const totalVoorschotten = apartment.energyYearVoorschotten.reduce((sum, month) => sum + month.amount, 0);
    console.log(`Total voorschotten for ${aptNum}: €${totalVoorschotten.toFixed(2)}`);
    
    // IMPORTANT: This is where the unitsForCalculation is selected - debug this value!
    const unitsForCalculation = currentYear ? usage.predictedTotal : usage.actualSoFar;
    console.log(`Units for calculation (${aptNum}):`, {
      isCurrentYear: currentYear,
      predictedTotal: usage.predictedTotal,
      actualSoFar: usage.actualSoFar,
      selected: unitsForCalculation
    });
    
    // Special case for zero usage
    const isZeroUsage = (usage.actualSoFar === 0 && currentYear === false) || 
                        (usage.predictedTotal === 0 && currentYear === true);
    
    if (isZeroUsage) {
      console.log(`⚠️ Zero usage detected for apartment ${aptNum}`);
    }
    
    // If units are undefined or NaN, try to fix with fallbacks
    let effectiveUnits = unitsForCalculation;
    if ((effectiveUnits === undefined || isNaN(effectiveUnits) || effectiveUnits === 0) && !isZeroUsage) {
      console.warn(`⚠️ Invalid units for ${aptNum}, attempting fallbacks...`);
      
      // Try fallbacks in this order
      if (!isNaN(usage.predictedTotal) && usage.predictedTotal > 0) {
        effectiveUnits = usage.predictedTotal;
        console.log(`Using predictedTotal as fallback: ${effectiveUnits}`);
      } else if (!isNaN(usage.actualSoFar) && usage.actualSoFar > 0) {
        effectiveUnits = usage.actualSoFar;
        console.log(`Using actualSoFar as fallback: ${effectiveUnits}`);
      } else if (!isNaN(usage.previousYearTotal) && usage.previousYearTotal > 0) {
        effectiveUnits = usage.previousYearTotal;
        console.log(`Using previousYearTotal as fallback: ${effectiveUnits}`);
      } else {
        // Last resort - use a default value based on apartment size
        const defaultValue = apartment.apartmentSize === 'SMALL' ? 250 : 375;
        effectiveUnits = defaultValue;
        console.log(`Using default value as fallback: ${effectiveUnits}`);
      }
    }
    
    // For zero usage, we only apply fixed costs
    const variableCosts = isZeroUsage ? 0 : effectiveUnits * priceSettings.variablePricePerUnit;
    const fixedUnits = APARTMENT_SIZES[apartment.apartmentSize] || APARTMENT_SIZES.LARGE;
    const fixedCosts = fixedUnits * priceSettings.fixedPricePerUnit;
    const totalPredictedCosts = variableCosts + fixedCosts;
    
    console.log(`Cost calculation for ${aptNum}:`, {
      effectiveUnits,
      variableCosts,
      fixedUnits,
      fixedCosts,
      totalPredictedCosts,
      isZeroUsage
    });
    
    // Calculate difference
    const difference = totalVoorschotten - totalPredictedCosts;
    const percentageDifference = totalPredictedCosts > 0 ? (difference / totalPredictedCosts) * 100 : 0;
    
    console.log(`Difference for ${aptNum}: €${difference.toFixed(2)} (${percentageDifference.toFixed(2)}%)`);
    
    // Determine status: Must be BOTH over 10% AND over €180 difference
    let status = 'OK';
    let statusColor = 'green';
    const absDifference = Math.abs(difference);
    const absPercentage = Math.abs(percentageDifference);
    
    if (absDifference > 180 && absPercentage > 10) {
      if (difference < 0) {
        status = 'Te weinig betaald';
        statusColor = 'red';
      } else {
        status = 'Te veel betaald';
        statusColor = 'orange';
      }
    }
    
    console.log(`Final status for ${aptNum}: ${status} (${statusColor})`);
    
    return { 
      status, 
      statusColor, 
      difference, 
      percentageDifference,
      effectiveUnits,
      isZeroUsage,
      variableCosts,
      fixedCosts,
      totalPredictedCosts
    };
  };

  // Generate voorschot history with separate periods
  const generateVoorschotHistory = (apartment) => {
    const energyYearVoorschotten = apartment.energyYearVoorschotten || [];
    
    // Group by energy year periods
    const julyToDecember = energyYearVoorschotten.filter(mv => mv.month >= 7 && mv.month <= 12);
    const januaryToJune = energyYearVoorschotten.filter(mv => mv.month >= 1 && mv.month <= 6);
    
    // Calculate averages for each period
    const julyDecAvg = julyToDecember.length > 0 
      ? julyToDecember.reduce((sum, mv) => sum + mv.amount, 0) / 6 
      : apartment.defaultMonthlyAmount || 0;
    
    const janJunAvg = januaryToJune.length > 0 
      ? januaryToJune.reduce((sum, mv) => sum + mv.amount, 0) / 6 
      : apartment.defaultMonthlyAmount || 0;
    
    return { julyDecAvg, janJunAvg };
  };

  // Generate email content for a specific apartment
  const generateEmailForApartment = (apartment) => {
    const aptNum = apartment.apartmentNumber;
    const usage = apartmentUsage[aptNum];
    const residents = residentsData[aptNum] || [];
    
    // Get Eigenaar residents
    const eigenaarResidents = residents.filter(r => r.rol === 'Eigenaar');
    
    if (!usage || eigenaarResidents.length === 0) {
      return null;
    }

    // Determine if this is current energy year
    const currentYear = isCurrentEnergyYear(selectedEnergyYear);

    // Calculate total voorschotten
    const totalVoorschotten = apartment.energyYearVoorschotten.reduce((sum, month) => sum + month.amount, 0);
    
    // Check for zero usage case
    const isZeroUsage = (usage.actualSoFar === 0 && !currentYear) || 
                        (usage.predictedTotal === 0 && currentYear);
    
    // Use the predicted total from the usage map with fallbacks
    let unitsForCalculation = currentYear ? usage.predictedTotal : usage.actualSoFar;
    
    // Add fallbacks if needed
    if ((unitsForCalculation === undefined || isNaN(unitsForCalculation) || unitsForCalculation === 0) && !isZeroUsage) {
      if (!isNaN(usage.predictedTotal) && usage.predictedTotal > 0) {
        unitsForCalculation = usage.predictedTotal;
      } else if (!isNaN(usage.actualSoFar) && usage.actualSoFar > 0) {
        unitsForCalculation = usage.actualSoFar;
      } else if (!isNaN(usage.previousYearTotal) && usage.previousYearTotal > 0) {
        unitsForCalculation = usage.previousYearTotal;
      } else {
        // Last resort - use a default value
        unitsForCalculation = apartment.apartmentSize === 'SMALL' ? 250 : 375;
      }
    }
    
    // For zero usage, variable costs are 0, but fixed costs still apply
    const variableCosts = isZeroUsage ? 0 : unitsForCalculation * priceSettings.variablePricePerUnit;
    const fixedUnits = APARTMENT_SIZES[apartment.apartmentSize] || APARTMENT_SIZES.LARGE;
    const fixedCosts = fixedUnits * priceSettings.fixedPricePerUnit;
    const totalPredictedCosts = variableCosts + fixedCosts;
    
    // Calculate difference
    const difference = totalVoorschotten - totalPredictedCosts;
    const percentageDifference = totalPredictedCosts > 0 ? 
      (difference / totalPredictedCosts) * 100 : 0;
      
    // UPDATED: Improved text for insufficient payments and zero usage
    let status, advice;
    const absDifference = Math.abs(difference);
    
    if (absDifference <= 180) {
      status = 'toereikend';
      advice = 'Je hoeft niets te doen.';
    } else if (difference < 0) {
      status = 'ontoereikend';
      const shortfall = Math.abs(difference);
      
      // Calculate remaining months until June of next year
      const currentDate = new Date();
      const currentMonth = currentDate.getMonth() + 1; // 1-12
      const currentYear = currentDate.getFullYear();
      
      let monthsLeft;
      if (currentMonth >= 7) {
        // July to December: months left until June of next year
        monthsLeft = 12 - currentMonth + 6;
      } else {
        // January to June: months left until June of current year
        monthsLeft = 6 - currentMonth + 1;
      }
      
      monthsLeft = Math.max(1, monthsLeft); // At least 1 month
      const extraPerMonth = Math.ceil(shortfall / monthsLeft);
      
      // Special text for zero usage case
      if (isZeroUsage) {
        advice = `Hoewel je geen gas verbruikt, worden er nog steeds vaste kosten in rekening gebracht. We raden je aan tot en met juni om €${shortfall.toFixed(0)} op zij te zetten om de afrekening te kunnen betalen, of je maandbedrag dat je aan stookkosten betaalt aan de VVE te verhogen met €${extraPerMonth} per maand.`;
      } else {
        advice = `We raden je aan tot en met juni om €${shortfall.toFixed(0)} op zij te zetten om de afrekening te kunnen betalen. Je mag je maandbedrag dat je aan stookkosten betaalt aan de VVE ook verhogen met €${extraPerMonth} per maand.`;
      }
    } else {
      status = 'teveel';
      if (isZeroUsage) {
        advice = 'Hoewel je geen gas verbruikt, betaal je momenteel te veel aan voorschotten. We raden je aan contact op te nemen met de penningmeester, wellicht kan je maandbedrag (tijdelijk) omlaag.';
      } else {
        advice = 'We raden je aan contact op te nemen met de penningmeester, wellicht kan je maandbedrag (tijdelijk) omlaag.';
      }
    }

    // Determine usage comparison
    let usageComparison = 'ongeveer zoals';
    if (isZeroUsage) {
      usageComparison = 'veel lager dan';
    } else if (usage.previousYearTotal && Math.abs(percentageDifference) > 10) {
      usageComparison = unitsForCalculation > usage.previousYearTotal ? 'hoger dan' : 'lager dan';
    }

    // UPDATED: Get Eigenaar resident name
    const primaryEigenaar = eigenaarResidents[0];
    const residentName = primaryEigenaar.naam || 'Bewoner';

    // Generate voorschot history
    const voorschotHistory = generateVoorschotHistory(apartment);

    return {
      apartmentNumber: aptNum,
      residentName,
      residentEmail: primaryEigenaar.email,
      subject: `Inzicht in jouw gasverbruik ${selectedEnergyYear}`,
      data: {
        datum: new Date().toLocaleDateString('nl-NL'),
        naam: residentName,
        huidigVoorschot: isZeroUsage ? 0 : Math.round(unitsForCalculation),
        verbruiktTotNu: Math.round(usage.actualSoFar),
        verwachtJaarverbruik: isZeroUsage ? 0 : Math.round(usage.predictedTotal),
        voorgaandJaarVerbruik: usage.previousYearTotal ? Math.round(usage.previousYearTotal) : 'N/A',
        verbruikVergelijking: usageComparison,
        juliDecemberBedrag: voorschotHistory.julyDecAvg,
        januariJuniBedrag: voorschotHistory.janJunAvg,
        verwachtTotaalbedragVoorschotten: totalVoorschotten,
        verwachteStookkosten: Math.round(totalPredictedCosts), 
        voorschotStatus: status,
        conclusie: advice,
        verschil: difference,
        energieJaar: selectedEnergyYear,
        isZeroUsage: isZeroUsage
      }
    };
  };

  // UPDATED: Generate email HTML content with Open Sans font and improved styling
  const generateEmailContent = (emailData) => {
    // Add special message for zero usage case
    const zeroUsageMsg = emailData.data.isZeroUsage ? 
      `<div style="background-color: #e8f4f0; padding: 15px; border-radius: 5px; border-left: 4px solid #246834; margin: 15px 0;">
        <p style="font-family: 'Open Sans', Arial, sans-serif; margin: 5px 0;"><strong>Speciale situatie:</strong> Er is geen gasverbruik geregistreerd voor jouw appartement in dit energiejaar. Je betaalt wel de vaste kosten voor de aansluiting.</p>
      </div>` : '';

    const content = `
      <div style="font-family: 'Open Sans', Arial, sans-serif; max-width: 600px; margin: 0 auto; line-height: 1.6; color: #333;">
        <h1 style="font-family: 'Open Sans', Arial, sans-serif; color: #246834; border-bottom: 2px solid #246834; padding-bottom: 10px;">
          Inzicht in jouw gasverbruik 
          <small style="font-size: 14px; font-weight: normal; color: #666;">${emailData.data.datum}</small>
        </h1>
        
        <p style="font-family: 'Open Sans', Arial, sans-serif;">Beste ${emailData.data.naam},</p>
        
        <p style="font-family: 'Open Sans', Arial, sans-serif;">Je betaalt maandelijks een voorschot voor je gasverbruik. In dit bericht lees je hoe je ervoor staat.</p>
        
        ${zeroUsageMsg}
        
        <h2 style="font-family: 'Open Sans', Arial, sans-serif; color: #246834; margin-top: 30px;">Verbruik</h2>
        <p style="font-family: 'Open Sans', Arial, sans-serif;">Je huidige voorschot is gebaseerd op het jaarverbruik van vorig jaar: <strong>${emailData.data.voorgaandJaarVerbruik} eenheden</strong>.</p>
        
        <p style="font-family: 'Open Sans', Arial, sans-serif;">Je verbruikte tot dit moment <strong>${emailData.data.verbruiktTotNu} eenheden</strong>, je verwachte jaarverbruik is <strong>${emailData.data.verwachtJaarverbruik} eenheden</strong>.</p>
        
        <p style="font-family: 'Open Sans', Arial, sans-serif;">Je verbruik is <strong>${emailData.data.verbruikVergelijking}</strong> verwacht.</p>
        
        <p style="font-family: 'Open Sans', Arial, sans-serif; font-style: italic; color: #666;"><em>Bij afwijkingen kleiner dan plus of min 10% gaan we uit van verwacht verbruik.</em></p>
        
        <h2 style="font-family: 'Open Sans', Arial, sans-serif; color: #246834; margin-top: 30px;">Moet je bijbetalen of krijg je terug?</h2>
        <p style="font-family: 'Open Sans', Arial, sans-serif;">Dat hangt af van je verbruik in de komende periode én van hoeveel voorschotten je gedurende het hele stookseizoen betaalde. Jouw voorschotten waren:</p>
        
        <ul style="font-family: 'Open Sans', Arial, sans-serif;">
          <li>Van juli - december betaalde je <strong>€${emailData.data.juliDecemberBedrag.toFixed(2)} per maand</strong></li>
          <li>Van januari - juni betaalde je <strong>€${emailData.data.januariJuniBedrag.toFixed(2)} per maand</strong></li>
        </ul>
        
        <h3 style="font-family: 'Open Sans', Arial, sans-serif; color: #246834; margin-top: 25px;">Vergelijking voorschotten en verwachte stookkosten per einde stookseizoen</h3>
        <div style="background-color: #f8fff4; padding: 15px; border-radius: 5px; border-left: 4px solid #246834; margin: 15px 0;">
          <p style="font-family: 'Open Sans', Arial, sans-serif; margin: 5px 0;"><strong>Te verwachten totaalbedrag aan voorschotten:</strong> €${emailData.data.verwachtTotaalbedragVoorschotten.toFixed(2)}</p>
          <p style="font-family: 'Open Sans', Arial, sans-serif; margin: 5px 0;"><strong>Te verwachten stookkosten op basis van huidige inzichten:</strong> €${emailData.data.verwachteStookkosten.toFixed(2)}</p>
          ${emailData.data.isZeroUsage ? 
            `<p style="font-family: 'Open Sans', Arial, sans-serif; margin: 5px 0;"><strong>Toelichting:</strong> Ondanks geen gasverbruik worden er vaste kosten in rekening gebracht voor de aansluiting.</p>` : ''
          }
        </div>
        
        <p style="font-family: 'Open Sans', Arial, sans-serif;">Je voorschotten zijn <strong>${
          emailData.data.voorschotStatus === 'toereikend' ? 'ongeveer toereikend' :
          emailData.data.voorschotStatus === 'teveel' ? 'je betaalde tot nu toe teveel' :
          'ontoereikend'
        }</strong>.</p>
        
        <p style="font-family: 'Open Sans', Arial, sans-serif; font-style: italic; color: #666;"><em>Bij afwijkingen van minder dan plus of min €180,- gaan we uit van toereikende voorschotten.</em></p>
        
        <h2 style="font-family: 'Open Sans', Arial, sans-serif; color: #246834; margin-top: 30px;">Conclusie</h2>
        <div style="background-color: ${emailData.data.voorschotStatus === 'ontoereikend' ? '#fff3cd' : '#d4edda'}; padding: 15px; border-radius: 5px; border-left: 4px solid ${emailData.data.voorschotStatus === 'ontoereikend' ? '#ffc107' : '#28a745'}; margin: 15px 0;">
          <p style="font-family: 'Open Sans', Arial, sans-serif; margin: 0;"><strong>${emailData.data.conclusie}</strong></p>
        </div>
        
        <div style="margin-top: 40px; padding-top: 20px; border-top: 1px solid #e0e7e0;">
          <p style="font-family: 'Open Sans', Arial, sans-serif;">
            Vriendelijke groet,<br>
            <strong>Henry de Cock</strong><br>
            Penningmeester
          </p>
        </div>
        
        <hr style="margin: 30px 0; border: none; border-top: 1px solid #e0e7e0;">
        <p style="font-family: 'Open Sans', Arial, sans-serif; font-size: 12px; color: #666; font-style: italic;">
          <em>We gaan ervan uit dat je geen betalingsachterstand hebt.</em>
        </p>
      </div>
    `;
    
    return content;
  };

  // Preview email for a specific apartment
  const previewEmail = (apartment) => {
    const emailData = generateEmailForApartment(apartment);
    if (emailData) {
      setEmailPreview(generateEmailContent(emailData));
      setPreviewApartment(apartment);
    }
  };

  // Create draft emails instead of sending immediately
  const createDraftEmails = async () => {
    if (selectedApartments.size === 0) {
      alert('Selecteer eerst appartementen om concepten voor te maken.');
      return;
    }

    const apartmentsToSend = apartments.filter(apt => selectedApartments.has(apt.apartmentNumber));
    
    // UPDATED: Check for Eigenaar email addresses specifically
    const emailsWithoutEigenaarAddress = apartmentsToSend.filter(apt => {
      const residents = residentsData[apt.apartmentNumber] || [];
      return !residents.some(r => r.email && r.rol === 'Eigenaar');
    });

    if (emailsWithoutEigenaarAddress.length > 0) {
      const apartmentNumbers = emailsWithoutEigenaarAddress.map(apt => apt.apartmentNumber).join(', ');
      if (!window.confirm(`De volgende appartementen hebben geen Eigenaar met e-mailadres: ${apartmentNumbers}. Doorgaan met de rest?`)) {
        return;
      }
    }

    // UPDATED: Filter for apartments with Eigenaar email addresses
    const validApartments = apartmentsToSend.filter(apt => {
      const residents = residentsData[apt.apartmentNumber] || [];
      return residents.some(r => r.email && r.rol === 'Eigenaar');
    });

    if (validApartments.length === 0) {
      alert('Geen appartementen met Eigenaar e-mailadres gevonden.');
      return;
    }

    if (!window.confirm(`${validApartments.length} concept e-mails maken in Gmail voor Eigenaren? Deze worden opgeslagen als concepten zodat je ze kunt bewerken voordat je ze verstuurt.`)) {
      return;
    }

    setSendingEmails(true);
    const results = { successful: [], failed: [] };

    try {
      for (const apartment of validApartments) {
        try {
          const emailData = generateEmailForApartment(apartment);
          if (!emailData) {
            results.failed.push({
              apartmentNumber: apartment.apartmentNumber,
              error: 'Kon e-mailgegevens niet genereren'
            });
            continue;
          }

          const emailContent = generateEmailContent(emailData);
          const finalHtml = generateEmailHtml(emailContent);

          // Create draft instead of sending
          console.log(`📧 Creating draft for apt ${apartment.apartmentNumber}, to: "${emailData.residentEmail}", subject: "${emailData.subject}"`);
          const response = await api.post('/api/create-draft-email', {
            to: emailData.residentEmail,
            from: 'bestuurpolderzicht@gmail.com',
            subject: emailData.subject,
            content: finalHtml
          }, {
            headers: {
              'Content-Type': 'application/json'
            }
          });

          if (response.status === 200) {
            results.successful.push({
              apartmentNumber: apartment.apartmentNumber,
              email: emailData.residentEmail,
              residentName: emailData.residentName
            });
          } else {
            results.failed.push({
              apartmentNumber: apartment.apartmentNumber,
              error: 'Concept maken mislukt'
            });
          }

          // Add small delay between requests
          await new Promise(resolve => setTimeout(resolve, 500));

        } catch (error) {
          const detail = error.response?.data?.message || error.response?.data?.error || error.message;
          console.error(`❌ Apt ${apartment.apartmentNumber} failed:`, error.response?.data || error.message);
          results.failed.push({
            apartmentNumber: apartment.apartmentNumber,
            error: detail
          });
        }
      }

      setSendResults(results);
      
      const successCount = results.successful.length;
      const totalCount = results.successful.length + results.failed.length;
      
      alert(`Concept e-mails maken voltooid!\nSuccesvol: ${successCount}/${totalCount}\nMislukt: ${results.failed.length}\n\nGa naar Gmail om de concepten te bekijken en te bewerken voordat je ze verstuurt.`);
      
    } catch (error) {
      console.error('❌ Error creating draft emails:', error);
      alert('Fout bij maken van concept e-mails: ' + error.message);
    } finally {
      setSendingEmails(false);
    }
  };

  // Toggle apartment selection
  const toggleApartmentSelection = (apartmentNumber) => {
    setSelectedApartments(prev => {
      const newSelection = new Set(prev);
      if (newSelection.has(apartmentNumber)) {
        newSelection.delete(apartmentNumber);
      } else {
        newSelection.add(apartmentNumber);
      }
      return newSelection;
    });
  };

  // UPDATED: Select all apartments with Eigenaar email
  const selectAllWithEmail = () => {
    const apartmentsWithEigenaarEmail = apartments.filter(apt => {
      const residents = residentsData[apt.apartmentNumber] || [];
      return residents.some(r => r.email && r.rol === 'Eigenaar');
    });
    
    setSelectedApartments(new Set(apartmentsWithEigenaarEmail.map(apt => apt.apartmentNumber)));
  };

  // Clear all selections
  const clearAllSelections = () => {
    setSelectedApartments(new Set());
  };

  // NEW: Sorted apartments using useMemo for performance
  const sortedApartments = useMemo(() => {
    if (!sortConfig.key) return apartments;

    return [...apartments].sort((a, b) => {
      let aVal, bVal;

      switch (sortConfig.key) {
        case 'apartmentNumber':
          aVal = parseInt(a.apartmentNumber) || 0;
          bVal = parseInt(b.apartmentNumber) || 0;
          break;
        
        case 'residentName':
          const aResidents = residentsData[a.apartmentNumber] || [];
          const bResidents = residentsData[b.apartmentNumber] || [];
          const aEigenaar = aResidents.filter(r => r.rol === 'Eigenaar');
          const bEigenaar = bResidents.filter(r => r.rol === 'Eigenaar');
          aVal = aEigenaar.length > 0 ? aEigenaar[0].naam : '';
          bVal = bEigenaar.length > 0 ? bEigenaar[0].naam : '';
          break;
        
        case 'email':
          const aResidentsEmail = residentsData[a.apartmentNumber] || [];
          const bResidentsEmail = residentsData[b.apartmentNumber] || [];
          aVal = aResidentsEmail.some(r => r.email && r.rol === 'Eigenaar') ? 1 : 0;
          bVal = bResidentsEmail.some(r => r.email && r.rol === 'Eigenaar') ? 1 : 0;
          break;

        case 'apartmentSize':
          aVal = a.apartmentSize === 'SMALL' ? 0 : 1;
          bVal = b.apartmentSize === 'SMALL' ? 0 : 1;
          break;
        
        case 'previousYearUsage':
          const aUsagePrev = apartmentUsage[a.apartmentNumber];
          const bUsagePrev = apartmentUsage[b.apartmentNumber];
          aVal = aUsagePrev ? aUsagePrev.previousYearTotal || 0 : 0;
          bVal = bUsagePrev ? bUsagePrev.previousYearTotal || 0 : 0;
          break;
          
        case 'actualUsage':
          const aUsageActual = apartmentUsage[a.apartmentNumber];
          const bUsageActual = apartmentUsage[b.apartmentNumber];
          aVal = aUsageActual ? aUsageActual.actualSoFar || 0 : 0;
          bVal = bUsageActual ? bUsageActual.actualSoFar || 0 : 0;
          break;
        
        case 'voorschottenTotal':
          aVal = a.energyYearVoorschotten.reduce((sum, month) => sum + month.amount, 0);
          bVal = b.energyYearVoorschotten.reduce((sum, month) => sum + month.amount, 0);
          break;
        
        case 'predictedUsage':
          const aUsage = apartmentUsage[a.apartmentNumber];
          const bUsage = apartmentUsage[b.apartmentNumber];
          aVal = aUsage ? aUsage.predictedTotal : 0;
          bVal = bUsage ? bUsage.predictedTotal : 0;
          break;
          
        case 'predictedCosts':
          const aStatus = calculateApartmentStatus(a);
          const bStatus = calculateApartmentStatus(b);
          aVal = aStatus.totalPredictedCosts || 0;
          bVal = bStatus.totalPredictedCosts || 0;
          break;
        
        case 'difference':
          const aDiff = calculateApartmentStatus(a);
          const bDiff = calculateApartmentStatus(b);
          aVal = aDiff.difference || 0;
          bVal = bDiff.difference || 0;
          break;
        
        case 'status':
          const aStatusVal = calculateApartmentStatus(a);
          const bStatusVal = calculateApartmentStatus(b);
          // Sort by status priority: "Te weinig betaald" first, then "Te veel betaald", then "OK"
          if (aStatusVal.status === 'Te weinig betaald') aVal = 0;
          else if (aStatusVal.status === 'Te veel betaald') aVal = 1;
          else aVal = 2;
          
          if (bStatusVal.status === 'Te weinig betaald') bVal = 0;
          else if (bStatusVal.status === 'Te veel betaald') bVal = 1;
          else bVal = 2;
          break;
        
        default:
          return 0;
      }

      // Handle null/undefined values
      if (aVal === null || aVal === undefined) aVal = '';
      if (bVal === null || bVal === undefined) bVal = '';

      // String comparison for text fields
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortConfig.direction === 'asc' 
          ? aVal.localeCompare(bVal) 
          : bVal.localeCompare(aVal);
      }

      // Numeric comparison
      return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
    });
  }, [apartments, sortConfig, residentsData, apartmentUsage]);

  if (loading) {
    return (
      <div className="email-generator">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Gegevens laden...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="email-generator">
      <div className="generator-header">
        <h3>📧 Gasverbruik Inzicht E-mails</h3>
        <p className="generator-description">
          Stuur Eigenaren persoonlijke inzichten over hun gasverbruik en voorschotten voor {selectedEnergyYear}
          <br />
          <small style={{ color: '#666', fontStyle: 'italic' }}>
            * Alleen Eigenaren ontvangen e-mails, geen huurders of partners
          </small>
        </p>
      </div>

      {/* Control Panel */}
      <div className="generator-controls">
        <div className="selection-controls">
          <button 
            className="btn-select-all"
            onClick={selectAllWithEmail}
            disabled={sendingEmails}
          >
            📧 Selecteer Alle Eigenaren met E-mail
          </button>
          <button 
            className="btn-clear-all"
            onClick={clearAllSelections}
            disabled={sendingEmails}
          >
            🗑️ Deselecteer Alles
          </button>
          <span className="selection-count">
            {selectedApartments.size} van {apartments.length} geselecteerd
          </span>
        </div>

        <div className="action-controls">
          <button 
            className="btn-create-drafts"
            onClick={createDraftEmails}
            disabled={sendingEmails || selectedApartments.size === 0}
          >
            {sendingEmails ? '📝 Concepten maken...' : '📝 Maak Concepten in Gmail'}
          </button>
        </div>
      </div>

      {/* Apartments List - UPDATED with sortable headers */}
      <div className="apartments-list-container">
        <table className="apartments-list">
          <thead>
            <tr>
              <th className="checkbox-col">
                <input
                  type="checkbox"
                  checked={selectedApartments.size === apartments.length && apartments.length > 0}
                  onChange={() => selectedApartments.size === apartments.length ? clearAllSelections() : selectAllWithEmail()}
                  disabled={sendingEmails}
                />
              </th>
              <th onClick={() => handleSort('apartmentNumber')} style={{ cursor: 'pointer' }}>
                🏠 App.{getSortIndicator('apartmentNumber')}
              </th>
              <th onClick={() => handleSort('residentName')} style={{ cursor: 'pointer' }}>
                👤 Eigenaar{getSortIndicator('residentName')}
              </th>
              <th onClick={() => handleSort('apartmentSize')} style={{ cursor: 'pointer' }}>
                📏 Grootte{getSortIndicator('apartmentSize')}
              </th>
              <th onClick={() => handleSort('previousYearUsage')} style={{ cursor: 'pointer' }}>
                📊 Vorig Jaar{getSortIndicator('previousYearUsage')}
              </th>
              <th onClick={() => handleSort('actualUsage')} style={{ cursor: 'pointer' }}>
                ⚡ Eenheden{getSortIndicator('actualUsage')}
              </th>
              <th onClick={() => handleSort('predictedUsage')} style={{ cursor: 'pointer' }}>
                🔮 Voorspelling{getSortIndicator('predictedUsage')}
              </th>
              <th onClick={() => handleSort('voorschottenTotal')} style={{ cursor: 'pointer' }}>
                💳 Voorschotten{getSortIndicator('voorschottenTotal')}
              </th>
              <th onClick={() => handleSort('predictedCosts')} style={{ cursor: 'pointer' }}>
                💰 Voorspelde Kosten{getSortIndicator('predictedCosts')}
              </th>
              <th onClick={() => handleSort('difference')} style={{ cursor: 'pointer' }}>
                📊 Verschil{getSortIndicator('difference')}
              </th>
              <th onClick={() => handleSort('status')} style={{ cursor: 'pointer' }}>
                🎯 Status{getSortIndicator('status')}
              </th>
              <th>E-mail Status</th>
              <th>Acties</th>
            </tr>
          </thead>
          <tbody>
            {sortedApartments.map(apartment => {
  const residents = residentsData[apartment.apartmentNumber] || [];
  const eigenaarResidents = residents.filter(r => r.rol === 'Eigenaar');
  const hasEigenaarEmail = eigenaarResidents.some(r => r.email);
  
  // Get usage with better error handling
  const usage = apartmentUsage[apartment.apartmentNumber];
  
  // Calculate voorschotten
  const totalVoorschotten = apartment.energyYearVoorschotten.reduce((sum, month) => sum + month.amount, 0);
  
  // Get status with enhanced fallback handling
  const analysisStatus = calculateApartmentStatus(apartment);
  
  // Check for zero usage
  const isZeroUsage = analysisStatus.isZeroUsage;
  
  // Extract values for display
  const previousYearUsage = usage?.previousYearTotal || 0;
  const actualUsage = usage?.actualSoFar || 0;
  const predictedUsage = usage?.predictedTotal || 0;
  const totalPredictedCosts = analysisStatus.totalPredictedCosts || 0;
  const difference = analysisStatus.difference || 0;
  
  // Format numbers for display
  const formatNumber = (num) => new Intl.NumberFormat('nl-NL').format(Math.round(num));
  const formatCurrency = (amount) => new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(amount);
  
  // Determine display value for predicted usage
  let predictedUsageDisplay;
  if (isZeroUsage) {
    predictedUsageDisplay = '0 eenheden (geen verbruik)';
  } else if (usage && typeof usage.predictedTotal === 'number' && !isNaN(usage.predictedTotal) && usage.predictedTotal > 0) {
    predictedUsageDisplay = `${formatNumber(usage.predictedTotal)} eenheden`;
  } else if (usage && typeof usage.actualSoFar === 'number' && !isNaN(usage.actualSoFar) && usage.actualSoFar > 0) {
    // Fallback to actual usage so far
    predictedUsageDisplay = `${formatNumber(usage.actualSoFar)} eenheden (schatting)`;
  } else if (usage && typeof usage.currentYearActual === 'number' && !isNaN(usage.currentYearActual) && usage.currentYearActual > 0) {
    // Fallback to current year actual
    predictedUsageDisplay = `${formatNumber(usage.currentYearActual)} eenheden (huidig)`;
  } else if (usage && typeof usage.previousYearTotal === 'number' && !isNaN(usage.previousYearTotal) && usage.previousYearTotal > 0) {
    // Ultimate fallback to previous year
    predictedUsageDisplay = `${formatNumber(usage.previousYearTotal)} eenheden (vorig jaar)`;
  } else {
    // Nothing available
    predictedUsageDisplay = 'Geen data';
  }
  
  return (
    <tr key={apartment.apartmentNumber} className={!hasEigenaarEmail ? 'no-email' : (isZeroUsage ? 'zero-usage' : '')}>
      <td className="checkbox-cell">
        <input
          type="checkbox"
          checked={selectedApartments.has(apartment.apartmentNumber)}
          onChange={() => toggleApartmentSelection(apartment.apartmentNumber)}
          disabled={sendingEmails || !hasEigenaarEmail}
        />
      </td>
      <td className="apartment-number">{apartment.apartmentNumber}</td>
      <td className="resident-info">
        {eigenaarResidents.length > 0 ? (
          eigenaarResidents.map((resident, idx) => (
            <div key={idx} className="resident-name">
              {resident.naam} <span className="role-badge">Eigenaar</span>
            </div>
          ))
        ) : (
          <span className="no-resident">Geen Eigenaar</span>
        )}
      </td>
      <td className="apartment-size">
        <span className={`size-badge ${apartment.apartmentSize.toLowerCase()}`}>
          {apartment.apartmentSize === 'SMALL' ? 'KLEIN' : 'GROOT'}
        </span>
      </td>
      <td className="previous-year-usage">
        {formatNumber(previousYearUsage)}
      </td>
      <td className="actual-usage">
        {formatNumber(actualUsage)}
      </td>
      <td className="predicted-usage">
        {isZeroUsage ? '0' : formatNumber(predictedUsage)}
        {isZeroUsage && <span className="zero-usage-badge">Geen verbruik</span>}
      </td>
      <td className="voorschotten-total">
        {formatCurrency(totalVoorschotten)}
        <div className="monthly-amount">
          <small>Ø {formatCurrency(totalVoorschotten / 12)}/mnd</small>
        </div>
      </td>
      <td className="predicted-costs">
        {formatCurrency(totalPredictedCosts)}
        <div className="cost-breakdown">
          <small>
            Var: {formatCurrency(analysisStatus.variableCosts || 0)} + 
            Vast: {formatCurrency(analysisStatus.fixedCosts || 0)}
          </small>
        </div>
      </td>
      <td className={`difference ${difference >= 0 ? 'positive' : 'negative'}`}>
        {formatCurrency(difference)}
      </td>
      <td className="analysis-status">
        <span className={`status-badge ${analysisStatus.statusColor}`}>
          {analysisStatus.status}
        </span>
        {analysisStatus.percentageDifference !== undefined && (
          <div className="status-details">
            <small>
              {analysisStatus.percentageDifference >= 0 ? '+' : ''}{analysisStatus.percentageDifference.toFixed(1)}%
            </small>
          </div>
        )}
      </td>
      <td className="email-status">
        {hasEigenaarEmail ? (
          <span className="status-ready">✅ Eigenaar E-mail</span>
        ) : (
          <span className="status-no-email">❌ Geen Eigenaar E-mail</span>
        )}
      </td>
      <td className="actions">
        <button 
          className="btn-preview"
          onClick={() => previewEmail(apartment)}
          disabled={sendingEmails}
          title="Bekijk e-mail voorbeeld"
        >
          👁️ Voorbeeld
        </button>
      </td>
    </tr>
  );
})}
          </tbody>
        </table>
      </div>

      {/* Email Preview Modal */}
      {emailPreview && (
        <div className="modal-overlay" onClick={() => setEmailPreview(null)}>
          <div className="modal-content email-preview-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>📧 E-mail Voorbeeld - Appartement {previewApartment?.apartmentNumber}</h3>
              <button className="modal-close" onClick={() => setEmailPreview(null)}>×</button>
            </div>
            <div className="modal-body">
              <div className="email-preview-container">
                <div 
                  className="email-preview-content"
                  dangerouslySetInnerHTML={{ 
                    __html: generateEmailHtml(emailPreview) 
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Send Results Modal */}
      {sendResults && (
        <div className="modal-overlay" onClick={() => setSendResults(null)}>
          <div className="modal-content send-results-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>📊 Concept Resultaten</h3>
              <button className="modal-close" onClick={() => setSendResults(null)}>×</button>
            </div>
            <div className="modal-body">
              <div className="results-summary">
                <div className="result-stat success">
                  <span className="stat-number">{sendResults.successful.length}</span>
                  <span className="stat-label">Concepten Gemaakt</span>
                </div>
                <div className="result-stat error">
                  <span className="stat-number">{sendResults.failed.length}</span>
                  <span className="stat-label">Mislukt</span>
                </div>
              </div>

              {sendResults.successful.length > 0 && (
                <div className="results-section">
                  <h4>✅ Concepten succesvol gemaakt voor Eigenaren</h4>
                  <p><strong>Ga naar Gmail om de concepten te bekijken en eventueel te bewerken voordat je ze verstuurt.</strong></p>
                  <div className="successful-list">
                    {sendResults.successful.map((result, idx) => (
                      <div key={idx} className="success-item">
                        {result.apartmentNumber} ({result.residentName})
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {sendResults.failed.length > 0 && (
                <div className="results-section">
                  <h4>❌ Mislukte concepten</h4>
                  <div className="failed-list">
                    {sendResults.failed.map((result, idx) => (
                      <div key={idx} className="failure-item">
                        <strong>Appartement {result.apartmentNumber}:</strong> {result.error}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VoorschotEmailGenerator;